// src/legal/useTermsStatus.js
// Has the signed-in user accepted the current Terms + Privacy Policy?
//
// state: 'loading' | 'accepted' | 'required' | 'error'
//
// FAILS CLOSED. If the status cannot be read the app does not proceed as though the
// user had accepted — it shows the error with a retry. The alternative is a legal gate
// that silently opens whenever its endpoint is down.
//
// That makes deploy order matter: migration 0033 must be applied to a database BEFORE
// code carrying this gate is deployed against it, or every user stops here.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

export default function useTermsStatus(enabled) {
  const [state, setState]     = useState('loading');
  const [version, setVersion] = useState(null);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setState('loading');
    setError('');
    try {
      const res  = await apiFetch('/api/me/terms');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      setVersion(data.version);
      setState(data.accepted ? 'accepted' : 'required');
    } catch (e) {
      setError(e.message);
      setState('error');
    }
  }, []);

  // Reset on sign-out, so the next person to sign in in this tab starts from 'loading'
  // and never inherits the previous user's 'accepted' for a render.
  useEffect(() => {
    if (enabled) load();
    else setState('loading');
  }, [enabled, load]);

  // Returns null on success, or an error message for the acceptance screen to show.
  const accept = useCallback(async () => {
    try {
      const res  = await apiFetch('/api/me/terms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) {
        // The text changed while this screen was open. Reload so they see the new one.
        setVersion(data.version || null);
        return data.error || 'The terms have been updated. Please review them again.';
      }
      if (!res.ok) return data.error || `Could not record acceptance (${res.status})`;
      setState('accepted');
      return null;
    } catch (e) {
      return e.message;
    }
  }, [version]);

  return { state, error, accept, retry: load };
}
